<template>
  <div class="h-full w-full min-w-0 overflow-hidden flex flex-col justify-between bg-white border-solid border-l border-0 border-gray-border">
    <div class="min-w-0 overflow-y-auto overflow-x-hidden flex-1">
      <div class="border-solid border-b border-0 border-gray-border px-8 pb-8 min-w-0">
        <input-with-error-message v-if="isRenameMode"
                                  id="index-editor-name-input"
                                  :value.sync="indexSourceName"
                                  :is-loading="isLoadingName"
                                  @escape="$emit('set-rename-mode', false)"
                                  @submit="handleRename"
                                  class="h-full mt-5 w-full"
        />
        <div v-else class="mt-8 pr-12 min-w-0 relative">
          <h3 class="font-semibold flex items-center min-w-0 overflow-hidden">
            <span class="cursor-pointer text-gray-dark hover:text-primary-focus hover:underline min-w-0 truncate"
                  @click="$emit('display-index-editor')"
            >{{ index.name }}</span>
            <span class="text-gray mx-1 flex-shrink-0">/</span>
            <span class="text-black min-w-0 truncate">{{ indexSource.name }}</span>
          </h3>
          <side-list-menu v-if="!isRenameMode"
                          :items="menuItems"
                          class="absolute top-0 right-0 -mt-1"
                          @rename="$emit('set-rename-mode', true)"
                          @delete="$emit('delete-index-source', indexSource)"
          />
        </div>
      </div>

      <settings-indexes-file v-if="sourceType === KNOWLEDGE_BASES.FILE"
                             :file.sync="fileSettings.file"
                             :url.sync="fileSettings.url"
                             @reset:fileSettings="resetFileSettings"
                             class="mt-8 px-8"
      />

      <settings-indexes-web-page v-if="sourceType === KNOWLEDGE_BASES.WEB_PAGE"
                                 :url.sync="webPageSettings.url"
                                 :prompt.sync="webPageSettings.prompt"
                                 :format.sync="webPageSettings.format"
                                 :headerKey.sync="webPageSettings.headerKey"
                                 :headerValue.sync="webPageSettings.headerValue"
                                 class="mt-8"
                                 :container-classes="'px-8 pb-8'"
                                 :index-source="indexSource"
                                 @onAddHeaderChecked="updateWebPageSettingsHeader($event.headerKey, $event.headerValue)"
      />

      <settings-indexes-xml-feed v-if="sourceType === KNOWLEDGE_BASES.XML_FEED"
                                 :url.sync="xmlFeedSettings.url"
                                 :item.sync="xmlFeedSettings.item"
                                 :item-name.sync="xmlFeedSettings.itemName"
                                 :item-id.sync="xmlFeedSettings.itemId"
                                 :category="xmlFeedSettings.category"
                                 :field-selection="xmlFeedSettings.fieldSelection"
                                 :show-field-selection="showFieldSelection"
                                 :manual-config="xmlFeedSettings.manualConfig"
                                 @update:manual-config="xmlFeedSettings.manualConfig = $event"
                                 @update:field-selection="updateFieldSelection"
                                 @update:category-selection="updateCategorySelection"
                                 @update:available-tags="availableTags = $event"
                                 class="mt-8 px-8"
      />
    </div>

    <div class="grid grid-cols-2 gap-4 mb-10 px-8">
      <busy-button id="saveButton"
                   :variant="saveButtonVariant"
                   class="col-span-1"
                   :is-loading="isSaving"
                   :is-disabled="isSaving"
                   @click="saveIndex"
      >
        {{ saveButtonText }}
      </busy-button>

      <busy-button id="reindexButton"
                   variant="bordered"
                   class="col-span-1"
                   :is-loading="isLoadingReindex"
                   :is-disabled="isLoadingReindex"
                   @click="$emit('start-reindex', index)"
      >
        Reindex now
      </busy-button>
    </div>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SideListMenu from '@/components/common/SideListMenu'
import toaster from '@/utils/toaster'
import { MENU_ITEMS, SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { SAVE_BUTTON_LABELS } from '@/utils/text.constants'
import clone from 'ramda/src/clone'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import SettingsIndexesFile from '@/components/settings/indexes/SettingsIndexesFile.vue'
import SettingsIndexesWebPage from '@/components/settings/indexes/SettingsIndexesWebPage.vue'
import SettingsIndexesXmlFeed from '@/components/settings/indexes/SettingsIndexesXmlFeed.vue'
import { applyFieldSelection, extractCategoryTag, extractFieldSelection } from '@/utils/xml-source-config.utils'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'
import errorUtils from '@/utils/error.utils'
import fileService from '@/services/amio/file.service'
import indexSourcesService from '@/services/amio/index-sources.service'
import previewModule from '@/store/modules/preview.module'

const UPDATE_BUTTON_LABEL = 'Save'
const SUCCESS_BUTTON_LABEL = 'Saved!'

export default {
  name: 'SettingsIndexSourceEditor',
  components: {
    SettingsIndexesFile,
    SettingsIndexesWebPage,
    SettingsIndexesXmlFeed,
    BusyButton,
    InputWithErrorMessage,
    SideListMenu
  },
  props: {
    index: {
      type: Object,
      required: true
    },
    indexSource: {
      type: Object,
      required: true
    },
    isLoadingName: {
      type: Boolean,
      default: false
    },
    isLoadingReindex: {
      type: Boolean,
      default: false
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    isRenameMode: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    indexSource: {
      immediate: true,
      handler(indexSource) {
        this.error = null
        if (this.sourceType === KNOWLEDGE_BASES.XML_FEED) {
          this.updateXMLFeedForm(indexSource)
        }
        if (this.sourceType === KNOWLEDGE_BASES.FILE) {
          this.updateFileForm(indexSource)
        }
        if (this.sourceType === KNOWLEDGE_BASES.WEB_PAGE) {
          this.updateWebPageForm(indexSource)
        }
      }
    }
  },
  data() {
    return {
      indexSourceName: '',
      webPageSettings: {
        url: '',
        prompt: null,
        format: 'txt',
        headerKey: null,
        headerValue: null
      },
      xmlFeedSettings: {
        manualConfig: false,
        url: '',
        item: '',
        itemName: '',
        itemId: '',
        category: '',
        fieldSelection: { embed_fields: [], payload_fields: {}, group_key: [] }
      },
      availableTags: [],
      fileSettings: {
        file: { name: '' }
      },
      saveButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      saveButtonText: UPDATE_BUTTON_LABEL,
      isEmpty,
      KNOWLEDGE_BASES
    }
  },
  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    menuItems() {
      const menuItemReindex = { ...MENU_ITEMS.reindexAllSources, label: 'Reindex' }
      if (this.index.sources.length === 1) return [MENU_ITEMS.rename, menuItemReindex]

      const menuItemDelete = { ...MENU_ITEMS.delete, label: 'Delete source' }
      return [MENU_ITEMS.rename, menuItemReindex, menuItemDelete]
    },
    sourceType() {
      switch (this.indexSource.type) {
        case 'file':
          return KNOWLEDGE_BASES.FILE
        case 'url':
          return KNOWLEDGE_BASES.XML_FEED
        case 'web_scraping':
          return KNOWLEDGE_BASES.WEB_PAGE
        default:
          return null
      }
    },
    // Qdrant XML sources can configure embed/payload/group-key per field,
    // shown once the feed preview has surfaced the available tags.
    showFieldSelection() {
      return this.index.provider === 'qdrant' &&
        this.sourceType === KNOWLEDGE_BASES.XML_FEED &&
        this.availableTags.length > 0
    }
  },
  methods: {
    updateWebPageSettingsHeader(headerKey, headerValue) {
      this.webPageSettings.headerKey = headerKey
      this.webPageSettings.headerValue = headerValue
    },
    updateWebPageForm(indexSource) {
      this.indexSourceName = indexSource.name
      const { url, config } = indexSource
      this.webPageSettings.url = url
      if (config?.prompt) this.webPageSettings.prompt = config.prompt
      if (config?.format) this.webPageSettings.format = config.format
      if (config?.header?.key) this.webPageSettings.headerKey = config.header.key
      if (config?.header?.value) this.webPageSettings.headerValue = config.header.value
      if (config?.error) this.error = { description: config.error }
    },

    updateXMLFeedForm(indexSource) {
      this.indexSourceName = indexSource.name
      const { url, config } = indexSource
      const tagMapping = config?.tag_mapping || {}
      this.xmlFeedSettings = {
        url,
        item: tagMapping.item || '',
        itemName: tagMapping.item_name || '',
        itemId: tagMapping.item_id || '',
        category: extractCategoryTag(config),
        // A source that already HAS a tag mapping is already configured, so the editor opens in manual
        // mode and shows it. Defaulting to false here would hide an existing configuration behind a
        // checkbox the user had no reason to look for — and, since the parent owns the state, the
        // checkbox could not have revealed it at all.
        manualConfig: !!tagMapping.item,
        // load the saved Qdrant field selection so the editor shows the current embed/payload/group_key
        fieldSelection: extractFieldSelection(config)
      }
    },

    updateFieldSelection(fieldSelection) {
      this.xmlFeedSettings.fieldSelection = fieldSelection
    },

    updateCategorySelection({ category, fieldSelection }) {
      this.xmlFeedSettings.category = category
      this.xmlFeedSettings.fieldSelection = fieldSelection
    },

    updateFileForm(indexSource) {
      this.indexSourceName = indexSource.name
      this.fileSettings.file = {
        name: this.getFileName(indexSource.url),
        url: indexSource.url,
        init: true
      }
    },

    getFileName(url) {
      const indexOfFile = url.indexOf('_FILE_')
      return indexOfFile !== -1 ?
        url.substring(indexOfFile + '_FILE_'.length) :
        url.split('/').pop()
    },

    updateScheduleParams(scheduleParams) {
      this.scheduleParams = scheduleParams
    },

    handleRename() {
      const updatedIndexSource = clone(this.indexSource)
      updatedIndexSource.name = this.indexSourceName
      this.$emit('rename', updatedIndexSource)
    },

    saveIndex() {
      this.error = null
      if (this.sourceType === KNOWLEDGE_BASES.XML_FEED) {
        this.saveXMLIndex()
      }
      if (this.sourceType === KNOWLEDGE_BASES.FILE) {
        this.saveFileIndex()
      }
      if (this.sourceType === KNOWLEDGE_BASES.WEB_PAGE) {
        this.saveWebPageIndex()
      }
    },

    async saveWebPageIndex() {
      try {
        const config = {
          prompt: this.webPageSettings.prompt,
          format: this.webPageSettings.format
        }
        if (this.webPageSettings.headerKey !== null || this.webPageSettings.headerValue !== null) {
          config.header = {
            key: this.webPageSettings.headerKey,
            value: this.webPageSettings.headerValue
          }
        }
        if (this.indexSource.config?.content) config.content = this.indexSource.config.content
        const patchData = {
          type: 'web_scraping',
          format: 'html',
          url: this.webPageSettings.url,
          name: this.indexSourceName,
          config: config
        }
        await indexSourcesService.update(this.index.id, this.indexSource.id, patchData)
        this.error = null
        this.$emit('reload-indexes')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.startButtonStateTimeout()
      }
    },

    async saveXMLIndex() {
      try {
        const { url, item, itemName, itemId, category, fieldSelection } = this.xmlFeedSettings
        const tagMapping = {
          item: item,
          item_name: itemName,
          item_id: itemId
        }
        const patchData = {
          type: 'url',
          format: 'xml',
          url: url,
          name: this.indexSourceName,
          // merge the Qdrant field selection (only when non-empty) — same as the create flow
          // Category is stored only as a payload field, never on tag_mapping.
          config: applyFieldSelection({
            tag_mapping: tagMapping
          }, fieldSelection, category)
        }
        await indexSourcesService.update(this.index.id, this.indexSource.id, patchData)
        this.error = null
        this.$emit('reload-indexes')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.startButtonStateTimeout()
      }
    },

    async saveFileIndex() {
      try {
        const patchData = { type: 'file', name: this.indexSourceName }
        if (this.fileSettings.file instanceof File) {
          const response = await fileService.create(this.fileSettings.file)
          patchData.format = response.format
          patchData.url = response.url
        } else if (this.fileSettings.url) {
          patchData.format = this.fileSettings.url.split('.').pop()
          patchData.url = this.fileSettings.url
        }

        await indexSourcesService.update(this.index.id, this.indexSource.id, patchData)
        this.error = null
        this.$emit('reload-indexes')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.startButtonStateTimeout()
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.saveButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      if (buttonState === 'DEFAULT') {
        this.saveButtonText = UPDATE_BUTTON_LABEL
      } else if (buttonState === 'SUCCESS') {
        this.saveButtonText = SUCCESS_BUTTON_LABEL
      } else {
        this.saveButtonText = SAVE_BUTTON_LABELS[buttonState]
      }
    },

    resetFileSettings() {
      this.fileSettings = { file: null, url: '' }
    },

    handleError(err) {
      if (err.message === 'Payload Too Large') {
        this.error = { description: 'File exceeds 1 MB limit.' }
        return
      }

      const errorMessage = head(err.response.data.details.errors)?.message || err.message
      if (errorUtils.isIndexCreateUpdateError(errorMessage)) {
        this.error = { description: errorMessage }
        return
      }

      this.error = errorMessage
      toaster.error(errorMessage)
    }
  }
}
</script>
